<script setup lang="ts">
    import { debounce } from '~/utils/debounce';
    import { onClickOutside, useMagicKeys } from '@vueuse/core';
    import { getSectionSearchPageSlug } from '~/utils/getSectionSearchPage';

    const currentSectionConfig = useCurrentSectionConfig();

    const input = ref<HTMLInputElement['value'] | null>(null);
    const inputEl = ref<HTMLInputElement | null>(null);
    const { country, lang } = useLanguage();
    const countryLanguages = useCountryLanguages();

    const currentSectionPath = computed(() => {
        if (!currentSectionConfig.value) return null;
        return resolveSectionPath(currentSectionConfig.value, lang.value, countryLanguages);
    });

    const { enter, escape } = useMagicKeys();

    const { t } = useI18n();

    const searchWrapper = ref<HTMLElement | null>(null);
    const showPreview = ref(false);

    const hasSearched = inject<Ref<boolean>>('hasSearched');

    const sendSearchQuery = async () => {
        if (!input?.value) return;

        const query = input.value;

        if (query.length < 1) return;

        const searchPageSlug = await getSectionSearchPageSlug();

        log.debug(
            `[HeaderSearch.vue] Sending search query: ${query}, Redirecting to search page with slug: ${searchPageSlug}`
        );
        const sectionPath = currentSectionPath.value;
        if (sectionPath === null) return;

        const searchPath = [sectionPath, searchPageSlug].filter(Boolean).join('/');
        await navigateTo({
            path: buildLocalizedPath(country.value, lang.value, searchPath),
            query: {
                q: query
            }
        });

        if (hasSearched) hasSearched.value = true;
    };

    const debouncedInput = ref(input.value);

    const updateDebouncedInput = debounce((value: string | null) => {
        debouncedInput.value = value;
    }, 300);

    watch(input, (value) => {
        if (value?.length && value.length <= 2) {
            searchResult.value = null;

            showPreview.value = !!value?.length;
            return;
        }

        if (hasSearched?.value) hasSearched.value = false;

        showPreview.value = !!value?.length;
        updateDebouncedInput(value);
    });

    const query = computed(() => {
        return debouncedInput?.value && debouncedInput?.value?.length > 2
            ? { q: debouncedInput.value, order: 'relevancy' }
            : {};
    });

    const { searchResult, pending } = useSammySearch(query, 'suggestion');

    const clearSearch = () => {
        input.value = null;
    };

    onClickOutside(searchWrapper, () => {
        showPreview.value = false;
    });

    const validSearchFocus = ref(false);
    const checkSearchFocus = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement | null;
        const wrapperEl = searchWrapper.value as HTMLElement | null;

        validSearchFocus.value = !!(wrapperEl && target && wrapperEl.contains(target));
    };

    watch(validSearchFocus, () => {
        showPreview.value = false;
    });

    watch(
        () => enter?.value,
        () => {
            if (inputEl.value && inputEl.value === document.activeElement) {
                sendSearchQuery();
            }
        }
    );
    watch(() => escape?.value, clearSearch);
    onMounted(() => {
        window.addEventListener('keyup', checkSearchFocus);

        if (useRoute().query?.q) {
            input.value = useRoute().query.q as string;
            requestAnimationFrame(() => {
                showPreview.value = false;
            });
        }
    });

    onUnmounted(() => {
        window.removeEventListener('keyup', checkSearchFocus);
    });
</script>

<template>
    <div
        ref="searchWrapper"
        class="search-wrapper"
    >
        <div class="search">
            <div class="input-wrapper">
                <input
                    ref="inputEl"
                    v-model="input"
                    data-testid="header-search-input"
                    type="text"
                    :placeholder="t('headerSearch.placeholder')"
                    :aria-label="t('headerSearch.ariaLabel')"
                    @click="
                        if (debouncedInput && debouncedInput.length > 2) {
                            showPreview = true;
                        }
                    "
                />
                <SharedCloseButton
                    v-if="input"
                    class="clear-search"
                    :label="t('search.clear')"
                    :icon-inverted="true"
                    @click="clearSearch()"
                />
            </div>
            <button
                class="--search"
                data-testid="header-search-submit-button"
                @click="sendSearchQuery"
            >
                <div class="sr-only">{{ t('headerSearch.srButtonText') }}</div>
                <img
                    v-if="!pending"
                    src="~/assets/icons/search.svg"
                    :alt="t('headerSearch.ariaLabelIcon')"
                    aria-hidden="true"
                />
                <SimpleLoadingSpinner
                    v-else
                    :size="23"
                />
            </button>
        </div>
        <div
            v-if="showPreview && !hasSearched"
            class="search-result-container"
        >
            <SearchResultPreview
                :search-hits="searchResult?.hits"
                :query
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/navigation/header-search.scss';
</style>
